import { Component, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Group } from '@microsoft/microsoft-graph-types-beta';
import { select, Store } from '@ngrx/store';
import { map, Observable, pairwise, skipUntil, startWith } from 'rxjs';
import { BaseComponent } from '../../../base/base.component';
import { initAuthenticationMethodsVoiceForm } from 'projects/angular-clarity/src/app/modules/sway/tools/schemas/graph/authentication-method-policy/voice/form';
import { VoiceAuthenticationMethodConfigurationForm } from 'projects/angular-clarity/src/app/modules/sway/tools/schemas/graph/authentication-method-policy/voice/model';
import { updateAuthenticationMethodsVoiceSchema } from 'projects/angular-clarity/src/app/modules/sway/tools/schemas/graph/authentication-method-policy/voice/schema';
import { client } from 'projects/angular-clarity/src/app/stores/client';

@Component({
    selector: 'app-baseline',
    templateUrl: './baseline.component.html',
    styleUrls: ['./baseline.component.scss', '../../../styles/baseline-style.scss'],
})
export class BaselineComponent extends BaseComponent implements OnInit {
    groups$: Observable<Map<string, Group>>;
    constructor(private store: Store<any>) {
        super();
    }
    ngOnInit(): void {
        this.groups$ = this.store
            .pipe(select(client(this.tenant_id).graph.groups.all))
            .pipe(skipUntil(this.store.pipe(select(client(this.tenant_id).graph.groups.loaded))))
            .pipe(
                map((groups) => {
                    return new Map(groups.map((group) => [group.id, group]));
                }),
            );

        const schema = JSON.parse(JSON.stringify(this.baseline.schema));
        this.form = initAuthenticationMethodsVoiceForm(schema);

        // Listen for state changes from disabled to enabled
        this.form
            .get('state')
            ?.valueChanges.pipe(startWith(this.form.get('state')?.value), pairwise())
            .subscribe(([previousState, currentState]) => {
                if (previousState === 'disabled' && currentState === 'enabled') {
                    this.setDefaultValuesIfNeeded();
                }
            });

        this.form.valueChanges.subscribe((_) => {
            const form = this.formValue;
            const updatedSchema = updateAuthenticationMethodsVoiceSchema(form);

            if (this.form.invalid) {
                this.baselineErrorChange.next({ remediate: true, save: true });
            } else {
                this.baselineErrorChange.next({ remediate: false, save: false });
            }

            this.baselineChange.next({
                ...this.baseline,
                schema: JSON.parse(JSON.stringify(updatedSchema)),
            });
        });
    }

    private setDefaultValuesIfNeeded(): void {
        // Set default for include targets to "all users"
        const includeTargetsGroup = this.form.get('includeTargets') as FormGroup;
        const includedGroupsArray = includeTargetsGroup.get('includedGroups') as FormArray;

        if (!includedGroupsArray || includedGroupsArray.length === 0) {
            includeTargetsGroup.get('groupInclusion')?.setValue(false);
            includedGroupsArray.clear();
            // Add 'all_users' to the array so it shows in the table
            const allUsersGroup = new FormGroup({
                id: new FormControl('all_users'),
            });
            includedGroupsArray.push(allUsersGroup);
        }

        // Set default for exclude targets to empty
        const excludeTargets = this.form.get('excludeTargets')?.value;
        if (!excludeTargets || excludeTargets.length === 0) {
            this.form.get('excludeTargets')?.setValue([]);
        }
    }

    get formValue(): VoiceAuthenticationMethodConfigurationForm {
        return this.form.getRawValue();
    }
}
